import type React from 'react';

export type BadgeVariant = 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'purple' | 'neutral';
export type BadgeSize = 'xs' | 'sm' | 'md' | 'lg';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: BadgeSize;
  dot?: boolean;
  icon?: React.ReactNode;
  children: React.ReactNode;
}

const Badge: React.FC<BadgeProps> = ({
  variant = 'default',
  size = 'sm',
  dot = false,
  icon,
  className = '',
  children,
  ...props
}) => {
  // Size classes
  const sizeClasses = {
    xs: {
      base: 'px-1.5 py-0.5 text-xs',
      icon: 'w-2.5 h-2.5',
      gap: 'gap-0.5'
    },
    sm: {
      base: 'px-2 py-1 text-xs',
      icon: 'w-3 h-3',
      gap: 'gap-1'
    },
    md: {
      base: 'px-2.5 py-1.5 text-sm',
      icon: 'w-4 h-4',
      gap: 'gap-1'
    },
    lg: {
      base: 'px-3 py-2 text-base',
      icon: 'w-5 h-5',
      gap: 'gap-1.5'
    }
  };

  // Variant classes
  const variantClasses = {
    default: 'bg-bg-surface text-text-primary border border-border-light',
    primary: 'bg-info-light text-info border border-info-border',
    success: 'bg-success-light text-success border border-success-border',
    warning: 'bg-warning-light text-warning border border-warning-border',
    danger: 'bg-danger-light text-danger border border-danger-border',
    info: 'bg-info-light text-info border border-info-border',
    purple: 'bg-purple-50 text-purple-700 border border-purple-100',
    neutral: 'bg-neutral-fill-lighter text-content-secondary border border-border-light'
  };

  const baseClasses = [
    'inline-flex',
    'items-center',
    'font-medium',
    'font-manrope',
    'rounded-md',
    'transition-all',
    'duration-200'
  ];

  const classes = [
    ...baseClasses,
    sizeClasses[size].base,
    variantClasses[variant],
    icon ? sizeClasses[size].gap : '',
    className
  ].filter(Boolean).join(' ');

  if (dot) {
    return (
      <span className={classes} {...props}>
        <span className={`${sizeClasses[size].icon} rounded-full bg-current opacity-60`} />
        {children}
      </span>
    );
  }

  return (
    <span className={classes} {...props}>
      {icon && (
        <span className={sizeClasses[size].icon}>
          {icon}
        </span>
      )}
      {children}
    </span>
  );
};

Badge.displayName = 'Badge';

export default Badge;